/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import {
  BaseButton,
  Grid,
  Icon,
  IconButton,
  Spinner,
  Spacer as Cd2Spacer,
  TextGroupMedium,
} from "@cd2/components"
import { Fields, Form2 } from "@cd2/forms"
import { P } from "@cd2/text"
import { doo, titleize } from "@cd2/tools"
import { format, startOfDay, sub } from "date-fns"
import React, { useState } from "react"
import { Link } from "react-router-dom"
import { buildSearchFilter } from "../../shared/search"
import { barscanWarehouseDespatchStatusLabel } from "../../shared/barscanWarehouseDespatchStatus"
import { FieldsRadioButtonSelect } from "../Components/FieldsRadioCheckbox"
import { Toolbar } from "../Components/Toolbar"
import { ToggleSwitch } from "../Components/ToggleSwitch"
import { ConfirmCancelButtons } from "../Components/Buttons/ConfirmCancelButtons"
import { DelayedRender } from "../Components/DelayedRender"
import { DropdownMenu } from "../Components/DropdownMenu"
import { EOSPagination } from "../Components/EOSPagination"
import { Frame } from "../Components/Frame"
import { useEnsureGlobalState } from "../Components/GlobalStateProvider"
import { IndexFilterWrapper } from "../Components/IndexFiltersWrapper"
import { PageWrapper } from "../Components/PageWrapper"
import { renderTable } from "../Components/renderTable"
import { SortSelect } from "../Components/SortSelect"
import { RmaLink } from "../Components/Tables/GoodsReceiptTable"
import { BackOrderLink, NoResultsAdvanced, NoResultsBlock } from "../Components/Tables/utils"
import { TableWrapper } from "../Components/TableWrapper"
import { buttonConfigs } from "../constants/buttonConfigs"
import { icons } from "../constants/icons"
import { pollingIntervals } from "../constants/pollingIntervals"
import { useAsyncCallback } from "../hooks/useAsyncCallback"
import { useAsyncEffect } from "../hooks/useAsyncEffect"
import { getInternalOrderFriendlyStatusName } from "../hooks/useExportInternalOrder"
import { useIndexTableState } from "../hooks/useIndexTableState"
import { usePollingInterval } from "../hooks/useInterval"
import { useManagedDialog } from "../hooks/useManagedDialog"
import { useReloadKey } from "../hooks/useReloadKey"
import { useSiteAlerts } from "../hooks/useSiteAlerts"
import { HoverBranchLoader } from "../hoverLoaders/HoverBranchLoader"
import { buildRoute, eRoutes } from "../routes"
import { ILoadingStatus } from "../types/common"
import { IRecord } from "../types/schema"
import { getTotals } from "../utils/getQuoteOrderTotals"
import { useSettings } from "../utils/useSettings"
import { delay, formatPrice } from "../utils/utils"
import { InternalOrderBarscanStatus, InternalOrderNoBarscanStatus } from "./GoodsReceiptsIndexPage"
import { QuickToolTip } from "../Components/QuickTooltip"
import { useApi } from "../hooks/useApi"
import { extractMessageFromError } from "../../server/utils/extractMessageFromError"
import {
  loadSourceOrderDespatchDates,
  sentStockTransfersAttribute,
  sourceOrderDespatchAttribute,
} from "../utils/sourceOrderDespatchDates"

const defaultSort = {
  by: "date" as any,
  direction: "DESC" as "ASC" | "DESC" | "asc" | "desc",
}

type Filters = {
  query: string
  productQuery: string
  pagination: { page: number; perPage: number }
  type: "order" | "sage_purchase_order" | undefined
  last30Days: boolean
  barscanDespatched: boolean
  sort: {
    by: "date" | "dispatchedDate" | "supplier" | "identifier"
    direction: "ASC" | "DESC" | "asc" | "desc"
  }
}

function toDate(value: string | Date | null | undefined) {
  return value ? new Date(value) : null
}

const typeOptions = [
  { label: "All", value: "all" },
  { label: "Goods Receipts", value: "order", description: "Internal transfers" },
  { label: "Sage Purchase Orders", value: "sage_purchase_order", description: "External supplier" },
]

/** Filters sit two across in the filter box, dropping to one when there is no room */
const filterColumnsCss = css`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  align-items: start;
  gap: 8px 12px;
`

/** A checkbox filter that keeps the toggle look the old filter buttons had */
function FilterToggle({
  label,
  value,
  onChange,
}: {
  label: string
  value: boolean
  onChange: (value: boolean) => void
}) {
  return (
    <div
      role="checkbox"
      aria-checked={value}
      tabIndex={0}
      onClick={() => onChange(!value)}
      onKeyDown={e => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault()
          onChange(!value)
        }
      }}
      css={css`
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 4px 0;
        cursor: pointer;
        font-size: 14px;
        user-select: none;
      `}
    >
      {/* the switch is painted by the row, so clicks are only handled in one place */}
      <div
        css={css`
          pointer-events: none;
          display: flex;
        `}
      >
        <ToggleSwitch value={value} onChange={onChange} small />
      </div>
      <span>{label}</span>
    </div>
  )
}

export function GoodsReceiptsPage() {
  const settings = useSettings()
  return (
    <React.Fragment>
      <Toolbar
        items={[
          {
            children: "View Completed Goods Receipts",
            icon: icons.orders,
            to: eRoutes["/goods-receipts/all"],
          },
          settings.showSagePurchaseOrdersOnGoodsReceiptsPage && {
            children: "View Completed Sage Purchase Orders",
            icon: icons.purchaseOrders,
            to: eRoutes["/sage-purchase-orders"],
          },
        ].filter(Boolean)}
      />
      <PageWrapper
        breadcrumbPath={["goodsReceipts"]}
        title="Goods Receipts"
        subtitle={<React.Fragment></React.Fragment>}
      >
        <OrdersList />
      </PageWrapper>
    </React.Fragment>
  )
}

type Row = {
  id: string
  type: "order" | "sage_purchase_order"
  identifier: string
  transferId: string
  sageDocNumber: string
  status: string
  branch: IRecord<"branches"> | null
  supplier: string | null
  deliveryMethod: string
  totalPrice: number | null
  date: Date | null
  /** When the order the transfer / purchase order was raised for was despatched */
  dispatchedDate: Date | null

  order?: IRecord<"orders"> & {
    return_request_source_rma?: IRecord<"rmas", "identifier"> | null
    childOrder: IRecord<"orders", "identifier"> | null
    parentOrder: IRecord<"orders", "identifier"> | null
  }
}

function OrdersList() {
  const globalState = useEnsureGlobalState()

  const api = useApi()
  const alerts = useSiteAlerts()
  const settings = useSettings()
  const [status, setStatus] = useState<ILoadingStatus>("loading")
  const { currentUser } = useEnsureGlobalState()
  const skeem = window.skeem

  type Result = {
    usedQuery: string
    usedProductQuery: string
    totalCount: number
    rows: Row[]
  }
  const [results, setResults] = useState<Result | null>(null)

  const [filter, setFilter] = useIndexTableState<Filters>("goods-receipts", {
    query: "",
    productQuery: "",
    sort: defaultSort,
    pagination: { page: 1, perPage: 20 },
    type: undefined,
    last30Days: false,
    barscanDespatched: false,
  })

  function fetchFilter({ query, productQuery }: { query: string; productQuery: string }) {
    return {
      and: [
        {
          matches: {
            internal_transfer_to_branch: currentUser.branch.id,
            internal_branch_transfer_status: {
              or: [
                InternalOrderBarscanStatus.exported,
                InternalOrderBarscanStatus.partially_exported,
                InternalOrderNoBarscanStatus.despatched,
                InternalOrderBarscanStatus.failed_source,
              ],
            },
          },
        },
        filter.last30Days && {
          and: [
            // exported recently
            { matches: { sage_exported_at: { gte: startOfDay(sub(new Date(), { days: 30 })) } } },
            {
              or: [
                {
                  // internal not-barscan is despatched
                  matches: {
                    internal_branch_transfer_status: InternalOrderNoBarscanStatus.despatched,
                  },
                },
                // barscan despatched
                { matches: { barscanWarehouseDespatchStatus: "despatched" } },
              ],
            },
          ],
        },
        filter.barscanDespatched && {
          matches: {
            barscanWarehouseDespatchStatus: "despatched",
          },
        },
        query && {
          or: [
            doo(() => {
              try {
                const value = Number(query)
                if (isNaN(value)) {
                  return null
                }
                if (!isFinite(value)) {
                  return null
                }
                return { matches: { barscanInternalOrderTransferId: value } }
              } catch (e) {
                console.error("Error parsing query as number:", e)
                return null
              }
            }),
            buildSearchFilter(
              [
                { attr: "identifier" },
                { attr: "sage_order_number" },
                { path: ["address", "postcode"] },
              ],
              query,
            ),
            {
              gte: [
                {
                  count: {
                    path: [
                      {
                        name: "return_request_source_rma",
                        filter: { matches: { identifier: { like: query } } },
                      },
                    ],
                  },
                },
                { value: 1 },
              ],
            },
            {
              exists: {
                path: [
                  {
                    name: "invoice_items",
                    filter: { matches: { invoice_document_number: { like: query } } },
                  },
                ],
              },
            },
          ].filter(Boolean),
        },
        productQuery && {
          gt: [
            {
              count: {
                path: [
                  "items",
                  {
                    name: "product",
                    filter: buildSearchFilter(
                      [{ attr: "stock_code" }, { attr: "manufacturerCode" }],
                      productQuery,
                    ),
                  },
                ],
              },
            },
            { value: 0 },
          ],
        },
      ].filter(Boolean),
    }
  }

  async function fetchOrders({ query, productQuery }: { query: string; productQuery: string }) {
    interface IFetchResult {
      orders: Order[]
      sagePurchaseOrders: SagePurchaseOrder[]
    }

    type Order = IRecord<
      "orders",
      | "identifier"
      | "barscanInternalOrderTransferId"
      | "barscanWarehouseDespatchStatus"
      | "barscan_despatched_at"
      | "internal_branch_transfer_status"
      | "delivery_method"
      | "updated_at"
      | "created_at"
      | "carriage_pennies"
    > & {
      branch: IRecord<"branches", "name"> | null
      user: IRecord<"skeem_users"> | null
      childOrder: IRecord<"orders", "identifier"> | null
      parentOrder: IRecord<"orders", "identifier"> | null
      items: IRecord<"order_items", "price_pennies" | "quantity" | "discountPercent">[]
      customer: IRecord<"customers", "tax_rate"> | null
      stock_transfers_generated: (IRecord<"stock_transfers", "transfer_date"> & {
        branch: IRecord<"branches"> | null
      })[]
      orderRequest: {
        purchase_order: {
          sourceOrder: IRecord<"orders", "sage_despatched_at"> | null
        } | null
      } | null
    }

    type SagePurchaseOrder = IRecord<
      "sage_purchase_orders",
      | "document_number"
      | "total_pennies"
      | "status"
      | "source_branch_id"
      | "document_date"
      | "requested_delivery_date"
    > & {
      purchase_order: IRecord<"purchase_orders", "identifier" | "created_at"> | null
      supplier: IRecord<"suppliers", "name"> | null
    }

    const skeemFilter = fetchFilter({ query, productQuery })
    const fetchResult = await skeem.fetch2<IFetchResult>({
      obj: {
        orders: {
          model: {
            name: "orders",
            attributes: [
              "identifier",
              "barscanInternalOrderTransferId",
              "barscanWarehouseDespatchStatus",
              "barscan_despatched_at",
              "internal_branch_transfer_status",
              "delivery_method",
              "updated_at",
              "created_at",
              "carriage_pennies",
              "user",
              { name: "return_request_source_rma", attributes: ["identifier"] },
              { name: "branch", attributes: ["name"] },
              { name: "childOrder", attributes: ["identifier"] },
              { name: "parentOrder", attributes: ["identifier"] },
              { name: "items", attributes: ["price_pennies", "quantity", "discountPercent"] },
              { name: "customer", attributes: ["tax_rate"] },
              sourceOrderDespatchAttribute,
              sentStockTransfersAttribute,
            ],
            filter: skeemFilter,
          },
        },
        sagePurchaseOrders: {
          model: {
            name: "sage_purchase_orders",
            attributes: [
              "document_number",
              "total_pennies",
              "status",
              "source_branch_id",
              "document_date",
              "requested_delivery_date",
              {
                name: "purchase_order",
                attributes: ["identifier", "created_at"],
              },
              { name: "supplier", attributes: ["name"] },
            ],
            filter: {
              and: [
                {
                  matches: {
                    all_received: false,
                    source_branch_id: currentUser.branch.id,
                    status: { not: "Completed" },
                  },
                },
                buildSearchFilter(
                  [{ attr: "document_number" }, { path: ["supplier", "name"] }],
                  query,
                ),
                filter.productQuery && {
                  gt: [
                    {
                      count: {
                        path: [
                          {
                            name: "items",
                            filter: buildSearchFilter(
                              [{ attr: "stock_code" }, { attr: "body" }],
                              productQuery,
                            ),
                          },
                        ],
                      },
                    },
                    { value: 0 },
                  ],
                },
              ].filter(Boolean),
            },
          },
        },
      },
    })

    const rows: Row[] = []

    if (filter.type !== "sage_purchase_order") {
      const dispatchedDates = await loadSourceOrderDespatchDates(fetchResult.orders)
      fetchResult.orders.forEach(order => {
        const eosStatus =
          getInternalOrderFriendlyStatusName(order.internal_branch_transfer_status ?? "", true) ||
          titleize(order.internal_branch_transfer_status ?? "")
        // Same gate as BarscanStatus: only barscan transfers show warehouse despatch status
        const despatchLabel =
          order.barscanInternalOrderTransferId &&
          barscanWarehouseDespatchStatusLabel(order.barscanWarehouseDespatchStatus)
        const row: Row = {
          id: order.id,
          type: "order",
          identifier: order.identifier,
          transferId: order.barscanInternalOrderTransferId
            ? String(order.barscanInternalOrderTransferId)
            : "",
          sageDocNumber: "",
          status: despatchLabel ? `${eosStatus} (${despatchLabel})` : eosStatus,
          branch: order.branch ?? null,
          supplier: order.branch?.name ?? null,
          deliveryMethod: order.delivery_method ?? "",
          totalPrice: getTotals(order.items, order.carriage_pennies, order.customer?.tax_rate)
            .totalPricePennies,
          date: order.created_at ? new Date(order.created_at) : null,
          dispatchedDate: dispatchedDates.get(order.id) ?? null,
          order,
        }
        rows.push(row)
      })
    }

    if (filter.type !== "order" && settings.showSagePurchaseOrdersOnGoodsReceiptsPage) {
      fetchResult.sagePurchaseOrders.forEach(sageOrder => {
        const date = sageOrder.purchase_order?.created_at ?? sageOrder.document_date
        const row: Row = {
          id: sageOrder.id,
          type: "sage_purchase_order",
          identifier: sageOrder.purchase_order?.identifier ?? sageOrder.document_number,
          transferId: "",
          sageDocNumber: sageOrder.document_number,
          status: titleize(sageOrder.status),
          branch: { id: sageOrder.source_branch_id },
          supplier: sageOrder.supplier?.name ?? null,
          deliveryMethod: "",
          totalPrice: sageOrder.total_pennies,
          date: date ? new Date(date) : null,
          dispatchedDate: toDate(sageOrder.requested_delivery_date),
        }
        rows.push(row)
      })
    }

    const getSortValue = (row: Row) => {
      if (filter.sort.by === "supplier") {
        return row.supplier || ""
      } else if (filter.sort.by === "identifier") {
        return row.identifier || ""
      } else if (filter.sort.by === "date") {
        return row.date ? new Date(row.date).getTime() : 0
      } else if (filter.sort.by === "dispatchedDate") {
        return row.dispatchedDate ? row.dispatchedDate.getTime() : 0
      } else {
        console.warn(`Unknown sort field: ${filter.sort.by}`)
        return ""
      }
    }
    rows.sort((a, b) => {
      const aValue = getSortValue(a)
      const bValue = getSortValue(b)
      if (filter.sort.direction === "ASC") {
        return aValue < bValue ? -1 : 1
      } else {
        return aValue > bValue ? -1 : 1
      }
    })

    return {
      count: rows.length,
      rows,
    }
  }

  const [refetchKey, triggerReload] = useReloadKey()
  usePollingInterval(() => triggerReload(), pollingIntervals.intervalTime)

  useAsyncEffect(
    async ({ isAlive }) => {
      if (!settings._loaded) {
        return
      }

      // Debounce
      await delay(300)
      if (!isAlive()) {
        return
      }

      if (status === "done") {
        setStatus("reFetching")
      }
      const fetchResult = await fetchOrders({
        query: filter.query,
        productQuery: filter.productQuery,
      })
      if (!isAlive()) {
        return
      }
      if (!fetchResult) {
        return
      }
      setResults({
        usedQuery: filter.query,
        usedProductQuery: filter.productQuery,
        totalCount: fetchResult.count,
        rows: fetchResult.rows,
      })
      setStatus("done")
    },
    [filter, refetchKey, settings._loaded],
  )

  const searchPending =
    results?.usedQuery !== filter.query ||
    results?.usedProductQuery !== filter.productQuery ||
    status === "reFetching"
  const tableStatus =
    !results || status === "loading"
      ? "loading"
      : status === "reFetching"
      ? "reloading"
      : results.totalCount === 0 && filter.query !== ""
      ? "noResultsInFilters"
      : results.totalCount === 0
      ? "noResults"
      : "done"

  const triggerUpdateTransferId = useUpdateTransferIdDialog()
  const handleSetTransferId = useAsyncCallback(async (orderId: string) => {
    const updated = await triggerUpdateTransferId({ orderId })
    if (updated) {
      triggerReload()
    }
  })

  const handleSyncSageReceipts = useAsyncCallback(async () => {
    try {
      const result = await api.syncSagePurchaseOrderReceiptsBatch({
        sourceBranchId: currentUser.branch.id,
      })
      alerts.genericSuccess(
        `Receipt sync complete: ${result.synced} purchase order${
          result.synced === 1 ? "" : "s"
        } checked${result.failed ? `, ${result.failed} failed` : ""}`,
      )
      triggerReload()
    } catch (e) {
      console.error(e)
      alerts.genericError(`Failed to sync receipts from Sage: ${extractMessageFromError(e)}`)
    }
  })

  return (
    <React.Fragment>
      <Grid gap={12}>
        <div
          css={css`
            gap: 8px;
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            align-items: end;
          `}
        >
          <IndexFilterWrapper>
            <Grid gap={12}>
              <Grid gap={8} alignItems="start">
                <Fields.Search
                  label="Search"
                  value={filter.query}
                  onChange={value =>
                    setFilter({
                      ...filter,
                      query: value,
                      pagination: { page: 1, perPage: filter.pagination.perPage },
                    })
                  }
                  suffix={
                    <Icon
                      icon={searchPending ? icons.spinner : icons.search}
                      spin={searchPending}
                    />
                  }
                />
                <Fields.Search
                  label="Search by Product Code"
                  value={filter.productQuery}
                  onChange={value =>
                    setFilter({
                      ...filter,
                      productQuery: value,
                      pagination: { page: 1, perPage: filter.pagination.perPage },
                    })
                  }
                  suffix={
                    <Icon
                      icon={searchPending ? icons.spinner : icons.search}
                      spin={searchPending}
                    />
                  }
                />
              </Grid>
              <div css={filterColumnsCss}>
                {settings.showSagePurchaseOrdersOnGoodsReceiptsPage && (
                  <FieldsRadioButtonSelect
                    name="goods-receipts-type"
                    options={typeOptions}
                    value={filter.type ?? "all"}
                    onChange={value =>
                      setFilter({
                        type: value === "all" ? undefined : (value as Filters["type"]),
                        pagination: { ...filter.pagination, page: 1 },
                      })
                    }
                  />
                )}
                <div>
                  <FilterToggle
                    label="Dispatched & Unreceived in the last 30 days"
                    value={!!filter.last30Days}
                    onChange={value =>
                      setFilter({
                        last30Days: value,
                        pagination: { ...filter.pagination, page: 1 },
                      })
                    }
                  />
                  <FilterToggle
                    label="Barscan Despatched"
                    value={!!filter.barscanDespatched}
                    onChange={value =>
                      setFilter({
                        barscanDespatched: value,
                        pagination: { ...filter.pagination, page: 1 },
                      })
                    }
                  />
                </div>
              </div>
            </Grid>
          </IndexFilterWrapper>
          <div>
            {settings.showSagePurchaseOrdersOnGoodsReceiptsPage && (
              <QuickToolTip
                maxWidth="320px"
                content={
                  <React.Fragment>
                    Pulls quantities already booked in on Sage onto the Sage purchase orders in this
                    list, and marks one fully received once its quantities match. Nothing is sent
                    back to Sage.
                  </React.Fragment>
                }
              >
                <BaseButton
                  {...buttonConfigs.small}
                  onClick={handleSyncSageReceipts}
                  disabled={handleSyncSageReceipts.running}
                  renderLeft={() =>
                    handleSyncSageReceipts.running ? (
                      <Spinner size={12} />
                    ) : (
                      <Icon icon={icons.refresh} size={14} />
                    )
                  }
                >
                  {handleSyncSageReceipts.running ? "Syncing…" : "Sync receipts from Sage"}
                </BaseButton>
              </QuickToolTip>
            )}
          </div>
        </div>
        <div
          css={css`
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            justify-content: space-between;
            align-items: end;
          `}
        >
          <SortSelect
            sortFields={["identifier", "supplier", "date", "dispatchedDate"] as const}
            onChange={value =>
              setFilter({
                ...filter,
                sort: { direction: "ASC", by: value },
              })
            }
            sort={filter.sort}
          />
          <EOSPagination
            currentPage={filter.pagination.page}
            gotoPage={(page: number) =>
              setFilter({ pagination: { perPage: filter.pagination.perPage, page } })
            }
            perPage={filter.pagination.perPage}
            setPerPage={(perPage: number) => setFilter({ pagination: { page: 1, perPage } })}
            maxPages={Math.ceil((results ? results.totalCount : 0) / filter.pagination.perPage)}
          />
        </div>
        <TableWrapper
          status={tableStatus}
          renderNoResults={() => <NoResultsBlock />}
          renderNoResultsInFilters={() => (
            <NoResultsAdvanced noDataMessage={"There are no internal orders."} actions={[]} />
          )}
          showTopScrollbar
        >
          {renderTable({
            data: (results?.rows ?? []).slice(
              (filter.pagination.page - 1) * filter.pagination.perPage,
              (filter.pagination.page - 1) * filter.pagination.perPage + filter.pagination.perPage,
            ),
            sort: filter.sort,
            onSort: sort => setFilter({ ...filter, sort }),
            columns: [
              {
                narrow: true,
                render: row => (
                  <div
                    css={css`
                      width: max-content;
                    `}
                  >
                    <Icon
                      icon={row.type === "order" ? icons.orders : icons.purchaseOrders}
                      size={20}
                    />
                  </div>
                ),
              },
              {
                heading: "ID",
                sortKey: "identifier" as const,
                render: row => (
                  <React.Fragment>
                    <Link
                      to={buildRoute(
                        row.type === "order"
                          ? eRoutes["/goods-receipts/:id"]
                          : eRoutes["/sage-purchase-orders/:id"],
                        { id: row.id },
                      )}
                    >
                      <b>{row.identifier}</b>
                    </Link>
                    {row.order && <BackOrderLink order={row.order} />}
                    <RmaLink order={row.order} />
                  </React.Fragment>
                ),
              },
              {
                heading: "Transfer ID",
                render: row => <b>{row.transferId}</b>,
              },
              {
                heading: "Sage Doc #",
                render: row => <b>{row.sageDocNumber}</b>,
              },
              {
                heading: "Status",
                render: row => row.status,
              },
              {
                heading: "Branch",
                render: row => (
                  <div>{row.branch ? <HoverBranchLoader id={row.branch.id} /> : null}</div>
                ),
              },
              {
                heading: "Supplier",
                sortKey: "supplier" as const,
                render: row => row.supplier ?? "-",
              },
              {
                heading: "Del. Method",
                render: row => row.deliveryMethod,
              },
              {
                heading: "Total Price",
                render: row =>
                  row.totalPrice
                    ? formatPrice(row.totalPrice, {
                        isPennies: true,
                        currency: globalState.currency,
                      })
                    : "-",
              },
              {
                heading: "Date",
                sortKey: "date" as const,
                render: row => (row.date ? format(row.date, "dd/MM/yyyy - HH:mm") : "-"),
              },
              {
                heading: "Dispatched",
                sortKey: "dispatchedDate" as const,
                render: row =>
                  row.dispatchedDate ? format(row.dispatchedDate, "dd/MM/yyyy - HH:mm") : "-",
              },
              {
                narrow: true,
                render: row => (
                  <DropdownMenu
                    items={[
                      {
                        children: "Set transfer ID",
                        onClick: () => handleSetTransferId(row.id),
                        disabled: handleSetTransferId.running || row.type !== "order",
                      },
                    ]}
                  >
                    <IconButton
                      icon={icons.ellipsis}
                      padding={8}
                      iconSize={14}
                      buttonStyle="text"
                      buttonColor="#999"
                      disabled={row.type !== "order"}
                      square
                    />
                  </DropdownMenu>
                ),
              },
            ],
          })}
        </TableWrapper>
        <EOSPagination
          currentPage={filter.pagination.page}
          gotoPage={(page: number) =>
            setFilter({ pagination: { perPage: filter.pagination.perPage, page } })
          }
          perPage={filter.pagination.perPage}
          setPerPage={(perPage: number) => setFilter({ pagination: { page: 1, perPage } })}
          maxPages={Math.ceil((results ? results.totalCount : 0) / filter.pagination.perPage)}
        />{" "}
      </Grid>
    </React.Fragment>
  )
}

export function useUpdateTransferIdDialog() {
  return useManagedDialog<boolean, { orderId: string }>({
    Component({ dialogData, onResolve, registerOutsideClick }) {
      registerOutsideClick(() => onResolve(false))

      const skeem = window.skeem
      const alerts = useSiteAlerts()

      type Order = IRecord<"orders", "identifier" | "barscanInternalOrderTransferId"> & {
        branch: IRecord<"branches", "name">
      }
      const [order, setOrder] = useState<Order | null>(null)

      useAsyncEffect(async () => {
        const order = await skeem.fetch2<
          IRecord<"orders", "identifier" | "barscanInternalOrderTransferId"> & {
            branch: IRecord<"branches", "name">
          }
        >({
          first: {
            name: "orders",
            attributes: [
              "identifier",
              "barscanInternalOrderTransferId",
              { name: "branch", attributes: ["name"] },
            ],
            matches: { id: dialogData.orderId },
          },
        })
        setOrder(order)
      }, [dialogData.orderId])

      const handleSubmit = useAsyncCallback(
        async ({ barscanInternalOrderTransferId }: { barscanInternalOrderTransferId: string }) => {
          try {
            if (barscanInternalOrderTransferId) {
              const existing = await skeem.fetch2<IRecord<"orders">>({
                first: { name: "orders", matches: { barscanInternalOrderTransferId } },
              })
              if (existing && existing.id !== dialogData.orderId) {
                alerts.genericError("Transfer ID already in use")
                return
              }
            }
            await skeem.mutate({
              orders: { update: { id: dialogData.orderId, barscanInternalOrderTransferId } },
            })
            onResolve(true)
            alerts.genericSuccess("Transfer ID set")
          } catch (e) {
            console.error(e)
            alerts.genericError("Error setting transfer ID, please try again")
          }
        },
      )

      if (!order) {
        return (
          <Frame>
            <DelayedRender>
              <Spinner color="black" />
            </DelayedRender>
          </Frame>
        )
      }

      return (
        <Form2.Form
          onSubmit={handleSubmit}
          initialData={{ barscanInternalOrderTransferId: order.barscanInternalOrderTransferId }}
        >
          <TextGroupMedium heading="Set internal transfer ID" />
          <Cd2Spacer />
          <Form2.Number label="Internal transfer ID" field="barscanInternalOrderTransferId" />
          <P size="small">Leave blank to clear the value.</P>
          <Cd2Spacer />
          <ConfirmCancelButtons onConfirm="submit" onCancel={() => onResolve(false)} />
        </Form2.Form>
      )
    },
  })
}
